import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, MapPin, Mail, Phone } from "lucide-react";

export default function AdminPendingMechanics() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async ({ silent = false } = {}) => {
    try {
      const { data } = await api.get("/admin/mechanics/pending");
      setItems(data);
    } finally { if (!silent) setLoading(false); }
  };
  useEffect(() => {
    load();
    const onEvent = () => load({ silent: true });
    window.addEventListener("afr-admin-event", onEvent);
    return () => window.removeEventListener("afr-admin-event", onEvent);
  }, []);

  const approve = async (uid) => {
    setBusy(uid);
    try {
      await api.post(`/admin/mechanics/${uid}/approve`, { approved: true });
      toast.success("Mechanic approved");
      load({ silent: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const reject = async (uid) => {
    setBusy(uid);
    try {
      await api.post(`/admin/mechanics/${uid}/reject`);
      toast.success("Mechanic rejected");
      load({ silent: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  return (
    <div>
      <div className="px-6 pt-8 pb-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#3CFF7E] font-display mb-1">Admin</div>
        <h1 className="font-display text-3xl font-black tracking-tight">Pending Mechanics</h1>
        <p className="text-sm text-[#9CA3AF] mt-1">
          {loading ? "Loading..." : `${items.length} awaiting approval`}
        </p>
      </div>

      <div className="px-6 pb-6 space-y-2">
        {!loading && items.length === 0 && (
          <div className="border border-dashed border-white/10 rounded-md p-8 text-center text-sm text-[#9CA3AF]">
            No mechanics awaiting approval.
          </div>
        )}
        {items.map((m) => (
          <div key={m.id} className="border border-white/10 bg-[#111827] rounded-md p-4" data-testid={`pending-${m.id}`}>
            <div className="mb-3">
              <div className="font-display text-lg font-black text-white">{m.name}</div>
              <div className="flex items-center gap-2 text-xs text-[#9CA3AF] mt-1">
                <Mail size={12} /> {m.email}
              </div>
              {m.phone && (
                <div className="flex items-center gap-2 text-xs text-[#9CA3AF] mt-0.5">
                  <Phone size={12} /> {m.phone}
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-[#9CA3AF] mt-0.5">
                <MapPin size={12} /> {m.location?.lat != null ? `${m.location.lat.toFixed(4)}, ${m.location.lng.toFixed(4)}` : "No location set"}
              </div>
              {m.skills?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {m.skills.map((s) => (
                    <span key={s} className="text-[10px] font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded border border-white/20 text-[#9CA3AF]">
                      {s}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => approve(m.id)}
                disabled={busy === m.id}
                data-testid={`approve-${m.id}`}
                className="h-11 bg-[#3CFF7E] text-[#0A0A0A] rounded-md font-display font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-1 disabled:opacity-50"
              >
                <CheckCircle size={14} /> Approve
              </button>
              <button
                onClick={() => reject(m.id)}
                disabled={busy === m.id}
                data-testid={`reject-${m.id}`}
                className="h-11 border border-[#F87171]/50 text-[#F87171] rounded-md font-display font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-1 disabled:opacity-50"
              >
                <XCircle size={14} /> Reject
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
